
import { defineComponent, VNode } from 'vue';
import SvgIcon from './SvgIcon.vue';

export default defineComponent({
  name: 'BaseButton',
  components: {
    SvgIcon
  },
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    fullWidth: {
      type: Boolean,
      default: false
    },
    to: {
      type: [String, Object],
      default: undefined
    },
    icon: {
      type: String,
      default: ''
    },
    variant: {
      type: String,
      default: 'primary'
    },
    destructive: {
      type: Boolean,
      default: false
    },
    success: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'md'
    },
    leadingIcon: {
      type: Boolean,
      default: false
    },
    trailingIcon: {
      type: Boolean,
      default: false
    },
    ariaLabel: {
      type: String,
      default: ''
    }
  },
  computed: {
    iconOnly(): boolean {
      return !this.$slots.default && (!!this.icon || !!this.computedIcon);
    },
    classes(): (string | Record<string, boolean>)[] {
      return [
        'base-button',
        `base-button--${this.variant}`,
        {
          [`base-button--${this.size}`]: this.size && this.variant !== 'link',
          'is-full-width': this.fullWidth,
          'is-destructive': this.destructive,
          'is-success': this.success,
          'leading-icon': this.leadingIcon,
          [`leading-icon-padding--${this.size}`]:
            this.leadingIcon && this.variant !== 'link',
          'trailing-icon': this.trailingIcon,
          [`trailing-icon-padding--${this.size}`]:
            this.trailingIcon && this.variant !== 'link',
          'icon-only': this.iconOnly,
          [`icon-only--${this.size}`]: this.iconOnly
        }
      ];
    },
    computedIcon(): string {
      if (this.loading) {
        return 'spinner';
      }
      if (this.success) {
        return 'tick';
      }
      if (this.destructive) {
        return 'bin';
      }
      return this.icon;
    }
  },
  created() {
    // Check has only one slot and slot is not a component
    if (this.$slots.default) {
      const hasComponent =
        this.$slots.default.length > 1 ||
        !!this.$slots.default[0].componentInstance;
      if (hasComponent) {
        console.warn(
          'It looks like you are providing more than text to your Button. If you are trying to use icons, please use appropriate props. Check documentation for more details.'
        );
      }
    }
  },
  methods: {
    checkIconProps(): void {
      if (this.leadingIcon && this.trailingIcon) {
        console.warn(
          'You should only use one icon per button, either remove the leadingIcon or the trailingIcon'
        );
      }
    },
    checkAriaLabel(): void {
      if (this.iconOnly && !this.ariaLabel) {
        console.warn(
          'It looks like you are missing an aria label which is required when the button holds no text. Please provide a label with the aria label prop.'
        );
      }
    }
  },
  render(h): VNode {
    this.checkIconProps();
    this.checkAriaLabel();
    let tag: string;
    let isAnchor = false;
    let isExternal = false;

    if (!this.to) {
      tag = 'button';
    } else {
      const routerTag = this.$nuxt ? 'nuxt-link' : 'router-link';
      isExternal = typeof this.to === 'string' && this.to.includes('://');
      const isDownloadLink = typeof this.$attrs.download === 'string';
      isAnchor = isExternal || isDownloadLink;
      tag = isAnchor ? 'a' : routerTag;
    }
    const props: Record<string, unknown> = {};
    if (tag !== 'button') {
      props[isAnchor ? 'href' : 'to'] = this.to;
    }

    const content: VNode | VNode[] = this.$slots.default;
    const hasContentIcon = content && (this.icon || this.computedIcon);

    return h(
      tag,
      {
        class: this.classes,
        attrs: {
          disabled: this.disabled || this.loading,
          'aria-label': this.ariaLabel
        },
        [tag === 'a' ? 'domProps' : 'props']: props
      },
      [
        this.computedIcon
          ? h('svg-icon', {
              props: {
                name: this.computedIcon || this.icon,
                size: this.size === 'lg' ? 'md' : 'sm'
              },
              class: [
                {
                  'u-margin-right-2x':
                    hasContentIcon && !this.leadingIcon && !this.trailingIcon,
                  [`leading-icon-spacing--${this.size}`]:
                    hasContentIcon && this.leadingIcon,
                  [`trailing-icon-spacing--${this.size}`]:
                    hasContentIcon && this.trailingIcon
                }
              ]
            })
          : null,
        content
      ]
    );
  }
});
